import { HttpHeaders } from "@angular/common/http";
import { OnDestroy } from "@angular/core";
import { environment } from "@environments";
import { ReplaySubject } from "rxjs";

import { IPhone } from "../../core/modules/users/interfaces/logon.interface";
import { User } from "../../core/modules/users/interfaces/user.interface";
import { AuthoritiesEnum } from "../enums/authorities.enum";
import { BlockedReasonsEnum } from "../enums/blocked-reasons.enum";
import { JustificationsEnum } from "../enums/justifications.enum";
import { SiteSection } from "../enums/site-section.enum";
import { UserProfileEnum } from "../enums/user-profile.enum";
import { AnalyticsService } from "../services/analytics.service";
import { DecodedToken } from "./interfaces/decoded-token.interface";

type Descriptions = keyof typeof BlockedReasonsEnum;
type Justifications = keyof typeof JustificationsEnum;

export const Utils = {
  emailRegex(): RegExp {
    return /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  },

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  },

  decodeToken(newJwt?: string): DecodedToken | null {
    try {
      const jwt = newJwt || this.getCookie("accessToken");
      if (jwt) {
        return JSON.parse(atob(jwt.split(".")[1]));
      }
    } catch {
      return null;
    }
    return null;
  },

  translateReason(description: Descriptions): string {
    if (description) {
      const newDescription = description.replace(".", "") as Descriptions;
      return BlockedReasonsEnum[newDescription];
    }

    return description;
  },

  translateJustification(description: Justifications): string {
    if (description) {
      const newDescription = description.replace(".", "") as Justifications;
      return JustificationsEnum[newDescription];
    }

    return description;
  },

  transformPhoneToObject(phone: string): IPhone {
    const regex = phone.replace(/\D/g, "");
    const regexMatched = regex.match(/(\d{0,2})(\d{2,})/);

    if (regexMatched !== null) {
      const [_, areaCode, phoneNumber] = regexMatched;

      return {
        areaCode,
        phoneNumber,
        regionCode: "55",
      };
    }

    return {};
  },

  canUserSeeUnblockedOption(user: User): boolean {
    if (user?.blockageTypes?.length) {
      const blockedByPassword = user.blockageTypes?.find((blockage) =>
        blockage.description?.includes("Blocked on RACF"),
      );
      const blockedByAgent = user.blockageTypes?.find((blockage) =>
        blockage.description?.includes("Blocked by screen"),
      );

      return !!(blockedByPassword || blockedByAgent);
    }

    return false;
  },

  checkTokenRole(jwt: string, role: string): boolean {
    return this.decodeToken(jwt)?.authorities?.includes(role) || false;
  },

  componentDestroyed(component: OnDestroy) {
    const oldNgOnDestroy = component.ngOnDestroy;
    const destroyed$ = new ReplaySubject<void>(1);
    component.ngOnDestroy = () => {
      oldNgOnDestroy.apply(component);
      destroyed$.next(undefined);
      destroyed$.complete();
    };
    return destroyed$;
  },

  locationHref(): string {
    return window.location.href;
  },

  generateDefaultHeader(): HttpHeaders {
    return new HttpHeaders()
      .set("content-type", "application/json")
      .set("Authorization", `Bearer ${this.getCookie("accessToken") || ""}`)
      .set("Must-Renovate", "true");
  },

  getOriginUrl(): string {
    if (this.isClientIdOfPMEApp()) {
      return environment.productMenuLoggedPMEURL;
    }

    return environment.productMenuLoggedURL;
  },

  getMainOriginUrl(): string {
    if (this.isClientIdOfPMEApp()) {
      return environment.productMenuMainPMEURL;
    }

    return environment.productMenuMainURL;
  },

  isClientIdOfPMEApp(): boolean {
    const clientId = localStorage.getItem("originClientID")?.toString() || "";

    switch (clientId) {
      case "5ecebf05f27e57302348fca2":
      case "5ecebfff3ea4e5292baf9692":
      case "5ecebf45aae366236fd0b584":
        return true;
      default:
        return false;
    }
  },

  dispatchUserList(service: AnalyticsService): void {
    const recertification = JSON.parse(
      localStorage.getItem("recertificationData") || "{}",
    );

    if (Object.keys(recertification).length) {
      service.doPageLoad(SiteSection.LISTAGEM_USUARIOS, {
        events: ["portalLogons_recertification"],
        recertification,
      });
    } else {
      service.doPageLoad(SiteSection.LISTAGEM_USUARIOS);
    }
  },

  setCookie(key: string, value: string, path = "/") {
    if (key && value) {
      try {
        const now = new Date();
        const ONE_HOUR = 1 * 60 * 60 * 1000;
        now.setTime(now.getTime() + ONE_HOUR);

        document.cookie = `${key}=${value}; expires=${now.toUTCString()}; Secure; path=${path}`;
        return true;
      } catch ($x) {
        return false;
      }
    }

    return false;
  },

  getCookie(cname: string) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (const i of ca) {
      let c = i;
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return "";
  },

  verifyUserProfile(): string {
    const userIsPontoFocal = Utils.checkTokenRole(
      Utils.getCookie("accessToken"),
      AuthoritiesEnum.ROLE_DT_LOGONPORTALMANAGER_ADMINMAINTENANCE,
    );

    const userIsGestor = Utils.checkTokenRole(
      Utils.getCookie("accessToken"),
      AuthoritiesEnum.ROLE_DT_LOGONPORTALVC_ADMIN,
    );

    if (userIsPontoFocal) {
      return UserProfileEnum.MANAGER;
    }

    return userIsGestor ? UserProfileEnum.VC : UserProfileEnum.USER;
  },

  deleteCookie(cname: string) {
    document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  },

  getBaseUrl(env: string ): string {
    let base = "";

    switch (env) {
      case "stg":
        base = "https://dev-api.serasaexperian.com.br";
        break;
      case "uat":
        base = "https://uat-api.serasaexperian.com.br";
        break;
      case "prod":
        base = "https://api.serasaexperian.com.br";
        break;
    }

    return base;
  },

  paramsToString(params: Map<string, string>): string {
    return Array.from(params.entries())
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  },

  doLogout(): void {
    this.deleteCookie('accessToken');
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('accessToken');
  },

  redirectToLogin(): void {
    const url = this.getMainOriginUrl();
    const buttonClick = document.createElement("a");
    buttonClick.href = url;
    buttonClick.click();
  }

};
